package org.zq.astrock_ai.quant

/**
 * 本地量化引擎：策略信号 + 简单回测 + 多策略共识。确定性计算，不编造行情。
 */
object QuantEngine {
    private val strategies: List<QuantStrategy> = listOf(
        DualMaStrategy(),
        BreakoutStrategy(),
        RsiReversalStrategy(),
    )

    private val consensusInfo = StrategyInfo(
        id = "consensus",
        name = "多策略共识",
        description = "综合双均线、区间突破、RSI 投票：≥2 票同向才形成明确方向。",
        defaultParams = emptyMap(),
    )

    fun listStrategies(): List<StrategyInfo> = strategies.map { it.info } + consensusInfo

    private fun resolveStrategy(id: String?): QuantStrategy {
        val key = id?.trim()?.lowercase().orEmpty()
        if (key == "consensus") error("consensus 请走 consensus()/buildPlan")
        return strategies.firstOrNull { it.info.id == key }
            ?: strategies.first { it.info.id == "dual_ma" }
    }

    fun getSignal(
        bars: List<QuantBar>,
        strategyId: String? = null,
        params: Map<String, String> = emptyMap(),
    ): QuantSignal {
        val key = strategyId?.trim()?.lowercase().orEmpty()
        if (key == "consensus" || key.isEmpty() && params["mode"] == "consensus") {
            val c = consensus(bars)
            val last = bars.lastOrNull()
            return QuantSignal(
                strategyId = consensusInfo.id,
                strategyName = consensusInfo.name,
                side = c.side,
                reason = c.summary,
                asOf = last?.date ?: "-",
                price = last?.close ?: 0.0,
            )
        }
        return resolveStrategy(strategyId).signal(bars, params)
    }

    fun runBacktest(
        bars: List<QuantBar>,
        strategyId: String? = null,
        params: Map<String, String> = emptyMap(),
        lookbackDays: Int? = null,
    ): BacktestResult {
        val sliced = slice(bars, lookbackDays)
        val key = strategyId?.trim()?.lowercase().orEmpty()
        if (key == "consensus") {
            return BacktestEngine.run(ConsensusStrategy(strategies), sliced, params)
        }
        return BacktestEngine.run(resolveStrategy(strategyId), sliced, params)
    }

    fun consensus(bars: List<QuantBar>): QuantConsensus {
        val votes = strategies.map { s ->
            val sig = s.signal(bars)
            ConsensusVote(
                strategyId = s.info.id,
                strategyName = s.info.name,
                side = sig.side,
                reason = sig.reason,
            )
        }
        val buy = votes.count { it.side == "BUY" }
        val sell = votes.count { it.side == "SELL" }
        val hold = votes.count { it.side == "HOLD" }
        val side = when {
            buy >= 2 && buy > sell -> "BUY"
            sell >= 2 && sell > buy -> "SELL"
            else -> "HOLD"
        }
        val score = buy - sell
        val voteText = votes.joinToString("；") { "${it.strategyName}:${it.side}" }
        val summary = when (side) {
            "BUY" -> "共识偏多（$buy 买 / $sell 卖 / $hold 观望）。$voteText"
            "SELL" -> "共识偏空（$buy 买 / $sell 卖 / $hold 观望）。$voteText"
            else -> "共识分歧或中性（$buy 买 / $sell 卖 / $hold 观望）。$voteText"
        }
        return QuantConsensus(
            side = side,
            buyCount = buy,
            sellCount = sell,
            holdCount = hold,
            score = score,
            summary = summary,
            votes = votes,
        )
    }

    fun buildPlan(
        bars: List<QuantBar>,
        strategyId: String? = null,
        params: Map<String, String> = emptyMap(),
        lookbackDays: Int? = null,
        position: PositionContext? = null,
    ): QuantPlan {
        val key = strategyId?.trim()?.lowercase().orEmpty().ifBlank { "dual_ma" }
        val consensus = if (key == "consensus") consensus(bars) else null
        val signal = getSignal(bars, key, params)
        val backtest = runBacktest(bars, key, params, lookbackDays)
        val strategyName = if (key == "consensus") consensusInfo.name else resolveStrategy(key).info.name
        val action = when (signal.side) {
            "BUY" -> "QUANT_BUY"
            "SELL" -> "QUANT_SELL"
            else -> "QUANT_HOLD"
        }
        val summary = when (signal.side) {
            "BUY" -> "量化信号偏多：${signal.reason}"
            "SELL" -> "量化信号偏空：${signal.reason}"
            else -> "量化信号中性：${signal.reason}"
        }
        var entryHint = when (signal.side) {
            "BUY" -> "可按策略规则考虑分批试探，并结合成交量确认"
            else -> "暂无新开仓信号，等待交叉/突破条件"
        }
        var exitHint = when (signal.side) {
            "SELL" -> "若持仓，优先按策略卖出信号减仓或离场"
            else -> "持仓可继续跟随策略；出现死叉/跌破时再评估"
        }
        var invalidation = "若收盘显著跌破信号价附近支撑，或回测失效条件触发，应停止加仓并复盘参数"
        val positionNote = buildPositionNote(signal.side, position, bars.lastOrNull()?.close)

        if (position?.hasPosition == true) {
            when (signal.side) {
                "BUY" -> {
                    entryHint = "已持仓时对加仓更谨慎：仅在回踩企稳且未靠近压力位时考虑小幅加仓"
                    exitHint = "继续持有观察；若后续出现共识转空或跌破支撑，优先减仓"
                }
                "SELL" -> {
                    entryHint = "当前不宜加仓"
                    exitHint = "已持仓且出现偏空信号，优先分批减仓或离场，勿摊平成本"
                    invalidation = "若快速跌破成本或近端支撑，应停止观望并执行风控"
                }
                else -> {
                    entryHint = "已持仓、信号中性：以持有为主，避免无计划加仓"
                    exitHint = "设置好失效价，跌破再处理，不因噪音频繁交易"
                }
            }
        } else if (position != null && !position.hasPosition) {
            when (signal.side) {
                "BUY" -> exitHint = "空仓可跟踪买入条件；未满足前保持观望"
                "SELL" -> {
                    entryHint = "空仓且偏空：不追空、不抄底，等待企稳或共识转多"
                    exitHint = "无持仓则无需卖出操作"
                }
                else -> entryHint = "空仓且中性：继续等待更明确的多策略共振"
            }
        }

        return QuantPlan(
            strategyId = if (key == "consensus") consensusInfo.id else resolveStrategy(key).info.id,
            strategyName = strategyName,
            action = action,
            summary = summary,
            entryHint = entryHint,
            exitHint = exitHint,
            invalidation = invalidation,
            signal = signal,
            backtest = backtest,
            consensus = consensus,
            positionNote = positionNote,
        )
    }

    fun toBundle(
        bars: List<QuantBar>,
        strategyId: String? = null,
        lookbackDays: Int? = null,
        position: PositionContext? = null,
        params: Map<String, String> = emptyMap(),
    ): QuantBundle {
        val plan = buildPlan(
            bars = bars,
            strategyId = strategyId,
            params = params,
            lookbackDays = lookbackDays,
            position = position,
        )
        return QuantBundle(
            strategies = listStrategies(),
            signal = plan.signal,
            backtest = plan.backtest,
            plan = plan,
            consensus = plan.consensus ?: if (strategyId == "consensus") consensus(bars) else null,
        )
    }

    private fun slice(bars: List<QuantBar>, lookbackDays: Int?): List<QuantBar> =
        if (lookbackDays != null && lookbackDays > 0 && bars.size > lookbackDays) {
            bars.takeLast(lookbackDays)
        } else {
            bars
        }

    private fun buildPositionNote(
        side: String,
        position: PositionContext?,
        lastPrice: Double?,
    ): String? {
        if (position == null || !position.hasPosition) return null
        val cost = position.costPrice ?: return "已持仓（未提供成本，仅给方向建议）"
        val price = position.currentPrice ?: lastPrice ?: return "已持仓，成本 $cost"
        val pnl = (price - cost) / cost * 100
        val qty = position.quantity
        val qtyText = qty?.let { "，数量 $it 股" }.orEmpty()
        val pnlText = "浮动约 ${if (pnl >= 0) "+" else ""}${round1(pnl)}%"
        return when (side) {
            "BUY" -> "持仓$qtyText，成本 $cost，现价 $price，$pnlText。信号偏多，加仓需更严格确认。"
            "SELL" -> "持仓$qtyText，成本 $cost，现价 $price，$pnlText。信号偏空，优先处理持仓风险。"
            else -> "持仓$qtyText，成本 $cost，现价 $price，$pnlText。信号中性，以持有与风控为主。"
        }
    }

    private fun round1(value: Double): String {
        val scaled = kotlin.math.round(value * 10.0) / 10.0
        val asLong = scaled.toLong()
        return if (scaled == asLong.toDouble()) asLong.toString() else scaled.toString()
    }
}

/** 日频投票：当日 ≥2 票同向则开平仓。 */
internal class ConsensusStrategy(
    private val members: List<QuantStrategy>,
) : QuantStrategy {
    override val info = StrategyInfo(
        id = "consensus",
        name = "多策略共识",
        description = "多策略投票回测",
    )

    override fun sides(bars: List<QuantBar>, params: Map<String, String>): List<QuantSide> {
        val memberSides = members.map { it.sides(bars, params) }
        return bars.indices.map { i ->
            var buy = 0
            var sell = 0
            memberSides.forEach { sides ->
                when (sides.getOrNull(i)) {
                    QuantSide.BUY -> buy++
                    QuantSide.SELL -> sell++
                    else -> Unit
                }
            }
            when {
                buy >= 2 && buy > sell -> QuantSide.BUY
                sell >= 2 && sell > buy -> QuantSide.SELL
                else -> QuantSide.HOLD
            }
        }
    }

    override fun signal(bars: List<QuantBar>, params: Map<String, String>): QuantSignal {
        val side = sides(bars, params).lastOrNull() ?: QuantSide.HOLD
        val last = bars.lastOrNull()
        return QuantSignal(
            strategyId = info.id,
            strategyName = info.name,
            side = side.name,
            reason = "共识投票信号 ${side.name}",
            asOf = last?.date ?: "-",
            price = last?.close ?: 0.0,
        )
    }
}
